import { put, call, takeEvery } from "redux-saga/effects";
import * as actions from "./actions";
import * as constants from "./constants";
import { setLocalState, removeLocalState, getLocalState } from "../../utils/localStorage";
import * as navigations from "../../utils/navigation";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";

import * as _service from "./service";

interface JwtPayload {
  email: string;
  first_name: string;
  last_name: string;
  user_id: string;
  exp: number;
}

function* loginSaga(loginPayload: any): Generator<any, void, any> {
  const { payload, history } = loginPayload;
  try {
    // Clear any stale tokens before login attempt
    yield call(removeLocalState, "access_token");
    yield call(removeLocalState, "refresh_token");
    yield call(removeLocalState, "_currentUser");

    // POST /api-token-auth/ with { email, password }
    const response = yield call(_service.login, payload);
    const { access, refresh, user: responseUser } = response.data;

    // Vérifier le slug de l'organisation — seul "tca" est autorisé
    const organisationSlug = responseUser?.organisation?.slug;
    if (organisationSlug !== "tca") {
      yield put(actions.loginFailed({ message: "Organisation non autorisée" }));
      yield call(toast.error, "L'utilisateur n'a pas les bons accès.");
      return;
    }

    // Decode JWT to extract user info
    const decoded: JwtPayload = jwtDecode(access);

    // Store tokens in localStorage
    yield call(setLocalState, "access_token", access);
    yield call(setLocalState, "refresh_token", refresh);

    // Build a temporary user object from JWT payload
    const jwtUser = {
      id: decoded.user_id,
      email: decoded.email,
      first_name: decoded.first_name,
      last_name: decoded.last_name,
      role: [] as Array<{ id: number; name: string }>,
    };

    // Dispatch success with tokens + jwt user data
    yield put(actions.loginSuccess({ access, refresh, user: jwtUser }));

    // Fetch full profile from /users/me/ to get roles
    try {
      const profileResponse = yield call(_service.getProfile);
      const profile = profileResponse.data;
      yield put(actions.fetchProfileSuccess(profile));
      yield call(setLocalState, "_currentUser", profile);

      // Redirect based on roles
      const roles = (profile.role || []).map((r: any) => r.name);
      const isAdmin = roles.includes('admin') || roles.includes('superadmin');
      const redirectRoute = isAdmin
        ? navigations.ESPACE_ADMIN
        : navigations.ESPACE_TCA;

      yield call(history, redirectRoute);
    } catch (profileError) {
      // Profile fetch failed but login succeeded — redirect to TCA by default
      yield call(setLocalState, "_currentUser", jwtUser);
      yield call(history, navigations.ESPACE_TCA);
    }

    yield call(toast.success, "Connexion réussie");
  } catch (error: any) {
    yield put(actions.loginFailed(error));

    // Handle specific error messages from API
    const status = error?.response?.status;
    if (status === 401) {
      yield call(toast.error, "Email ou mot de passe incorrect.");
    } else if (status === 429) {
      yield call(toast.error, "Trop de tentatives. Veuillez réessayer plus tard.");
    } else {
      yield call(toast.error, "Erreur de connexion. Veuillez réessayer.");
    }
  }
}

function* fetchProfileSaga(): Generator<any, void, any> {
  try {
    const response = yield call(_service.getProfile);
    yield put(actions.fetchProfileSuccess(response.data));
    yield call(setLocalState, "_currentUser", response.data);
  } catch (error) {
    yield put(actions.fetchProfileFailed(error));
  }
}

function* updateProfileSaga(action: any): Generator<any, void, any> {
  try {
    const response = yield call(_service.updateProfile, action.payload);
    yield put(actions.updateProfileSuccess(response.data));
    yield call(setLocalState, "_currentUser", response.data);
    yield call(toast.success, "Profil mis à jour avec succès.");
    if (action.callback) action.callback();
  } catch (error: any) {
    yield put(actions.updateProfileFailed(error));
    const data = error?.response?.data;
    if (data?.detail) {
      yield call(toast.error, data.detail);
    } else {
      yield call(toast.error, "Erreur lors de la mise à jour du profil.");
    }
  }
}

function* changePasswordSaga(action: any): Generator<any, void, any> {
  try {
    yield call(_service.changePassword, action.payload);
    yield put(actions.changePasswordSuccess());
    yield call(toast.success, "Mot de passe modifié avec succès.");
    if (action.callback) action.callback();
  } catch (error: any) {
    yield put(actions.changePasswordFailed(error));
    const data = error?.response?.data;
    if (data?.detail) {
      yield call(toast.error, data.detail);
    } else if (data?.old_password) {
      yield call(toast.error, "Le mot de passe actuel est incorrect.");
    } else if (data?.new_password) {
      yield call(toast.error, Array.isArray(data.new_password) ? data.new_password[0] : data.new_password);
    } else {
      yield call(toast.error, "Erreur lors du changement de mot de passe.");
    }
  }
}

function* logoutSaga(logoutPayload: any): Generator<any, void, any> {
  const { history } = logoutPayload;
  try {
    // Blacklist the refresh token on the server
    const refreshToken = yield call(getLocalState, "refresh_token");
    if (refreshToken) {
      try {
        yield call(_service.logout, refreshToken);
      } catch (e) {
        // Ignore logout API errors (token may already be expired)
      }
    }

    yield call(removeLocalState, "_currentUser");
    yield call(removeLocalState, "access_token");
    yield call(removeLocalState, "refresh_token");

    yield call(history, navigations.LOGIN);
  } catch (error) {
    console.error("Erreur lors de la déconnexion:", error);
  }
}

export default function* authSaga() {
  yield takeEvery(constants.LOGIN_REQUESTING, loginSaga);
  yield takeEvery(constants.FETCH_PROFILE_REQUESTING, fetchProfileSaga);
  yield takeEvery(constants.UPDATE_PROFILE_REQUESTING, updateProfileSaga);
  yield takeEvery(constants.CHANGE_PASSWORD_REQUESTING, changePasswordSaga);
  yield takeEvery(constants.LOGOUT, logoutSaga);
}
